import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Zap, ArrowRight, Check, Activity, Link as LinkIcon, RefreshCw, Menu, X, Star, CreditCard } from 'lucide-react';
import { motion, useMotionValue, useTransform, useSpring, useInView } from 'framer-motion';
import Logo from '../components/Logo';

// --- Reusable Components & Variants ---

const sentenceVariants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05, delayChildren: 0.1 },
  },
};

const wordVariants = {
  hidden: { opacity: 0, y: 15, filter: 'blur(8px)' },
  visible: { 
    opacity: 1, 
    y: 0, 
    filter: 'blur(0px)',
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } 
  },
};

const AnimatedText = ({ text, highlightWords = [], className, ariaLabel }) => {
  return (
    <motion.h1 
      variants={sentenceVariants} 
      initial="hidden" 
      animate="visible" 
      className={className}
      aria-label={ariaLabel || text}
    >
      {text.split(' ').map((word, index) => (
        <React.Fragment key={index}>
          <motion.span variants={wordVariants} className="inline-block" aria-hidden="true">
            {highlightWords.includes(word) ? (
              <span className="animate-glisten bg-clip-text text-transparent">
                {word}
              </span>
            ) : word}
          </motion.span>
          {' '}
        </React.Fragment>
      ))}
    </motion.h1>
  );
};

// Premium Glassmorphism Card
const SpotlightCard = ({ children, className, style, spotColor = "rgba(255,255,255,0.03)" }) => {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    cardRef.current.style.setProperty('--mouse-x', `${x}px`);
    cardRef.current.style.setProperty('--mouse-y', `${y}px`);
  };

  return (
    <motion.div 
      ref={cardRef}
      onMouseMove={handleMouseMove}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`relative rounded-3xl bg-white/[0.02] border border-white/10 p-10 md:p-12 overflow-hidden flex flex-col group backdrop-blur-xl shadow-2xl ${className}`}
      style={style}
    >
      <div className="absolute inset-0 z-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: `radial-gradient(800px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), ${spotColor}, transparent 40%)` }}></div>
      {/* Subtle top inner highlight */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      <div className="relative z-10 h-full flex flex-col">
        {children}
      </div>
    </motion.div>
  );
};

export default function Landing() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef);
  
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const mouseXSpring = useSpring(x, { stiffness: 100, damping: 20 });
  const mouseYSpring = useSpring(y, { stiffness: 100, damping: 20 });

  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["8deg", "-8deg"]);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-8deg", "8deg"]);

  const handleHeroMouseMove = (event) => {
    if (!isHeroInView) return;
    const rect = heroRef.current.getBoundingClientRect();
    const mouseX = (event.clientX - rect.left) / rect.width - 0.5;
    const mouseY = (event.clientY - rect.top) / rect.height - 0.5;
    x.set(mouseX);
    y.set(mouseY);
  };

  return (
    <div className="bg-[#0B0F19] text-slate-100 min-h-screen font-sans overflow-x-hidden selection:bg-indigo-500/30 w-full flex flex-col relative">
      
      {/* Ambient FinTech Gradients */}
      <div className="fixed top-[-20%] left-[-10%] w-[50vw] h-[50vw] rounded-full bg-indigo-900/20 blur-[150px] pointer-events-none -z-10" />
      <div className="fixed bottom-[-20%] right-[-10%] w-[60vw] h-[60vw] rounded-full bg-emerald-900/10 blur-[150px] pointer-events-none -z-10" />
      <div className="fixed top-[40%] left-[50%] -translate-x-1/2 w-[80vw] h-[30vw] rounded-[100%] bg-purple-900/10 blur-[120px] pointer-events-none -z-10" />

      {/* Navigation Bar */}
      <nav className="w-full flex justify-between items-center px-6 md:px-12 py-4 border-b border-white/5 bg-[#0B0F19]/60 backdrop-blur-xl sticky top-0 z-50">
        <div className="flex items-center gap-12">
          <button 
            onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})} 
            className="flex items-center gap-3 font-bold text-2xl hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-lg p-1 text-white tracking-tight"
            aria-label="Scroll to top"
          >
            <Logo size={28} />
            <span>Thread</span>
          </button>
          
          <div className="hidden md:flex gap-8 font-medium text-sm tracking-wide">
            <button onClick={() => document.getElementById('features').scrollIntoView({behavior: 'smooth'})} className="text-slate-400 hover:text-white transition-colors">Platform</button>
            <button onClick={() => document.getElementById('pricing').scrollIntoView({behavior: 'smooth'})} className="text-slate-400 hover:text-white transition-colors">Pricing</button>
          </div>
        </div>

        <div className="hidden md:flex gap-6 items-center text-sm">
          <Link to="/login" className="text-slate-400 hover:text-white font-medium transition-colors">Log in</Link>
          <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
            <Link to="/login" className="px-5 py-2.5 bg-white text-[#0B0F19] hover:bg-slate-200 rounded-full font-semibold transition-colors shadow-lg shadow-white/10">Start free trial</Link>
          </motion.div>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden p-2 text-slate-400 hover:text-white focus:outline-none"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle mobile menu"
        >
          {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </nav>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden fixed top-[81px] left-0 right-0 bg-[#0B0F19]/95 backdrop-blur-3xl border-b border-white/10 p-6 flex flex-col gap-6 z-40 shadow-2xl"
        >
          <button onClick={() => { setIsMobileMenuOpen(false); document.getElementById('features').scrollIntoView({behavior: 'smooth'}); }} className="text-left text-lg font-medium text-slate-300 hover:text-white">Platform</button>
          <button onClick={() => { setIsMobileMenuOpen(false); document.getElementById('pricing').scrollIntoView({behavior: 'smooth'}); }} className="text-left text-lg font-medium text-slate-300 hover:text-white">Pricing</button>
          <hr className="border-white/10" />
          <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-medium text-slate-300 hover:text-white">Log in</Link>
          <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="px-6 py-3 bg-white text-[#0B0F19] rounded-xl font-semibold text-center">Start free trial</Link>
        </motion.div>
      )}

      <main className="w-full flex-grow flex flex-col items-center">
        {/* Hero Section */}
        <section 
          id="hero"
          ref={heroRef}
          onMouseMove={handleHeroMouseMove} 
          className="w-full pt-10 md:pt-14 pb-8 md:pb-12 px-6 flex flex-col items-center text-center relative"
        >
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/[0.03] border border-white/10 rounded-full mb-3 text-xs font-semibold tracking-wide text-indigo-300 backdrop-blur-md uppercase"
          >
            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full shadow-[0_0_8px_#34d399]"></span> 
            System Operational
          </motion.div>
          
          <AnimatedText 
            text="Autonomous Wealth Routing Engine."
            highlightWords={["Routing", "Engine."]}
            ariaLabel="Autonomous Wealth Routing Engine."
            className="text-[clamp(3.5rem,7vw,7rem)] font-bold mb-4 leading-[1.02] tracking-tighter max-w-[1200px] text-white"
          />
          
          <motion.p 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="text-xl md:text-2xl text-slate-400 max-w-[700px] mb-6 leading-relaxed font-light"
          >
            Connect your institutions. Define your rules. Thread executes your wealth allocation programmatically with microsecond precision.
          </motion.p>
          
          {/* CTA Buttons Row */}
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-5 items-center justify-center z-20 w-full"
          >
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="w-full sm:w-auto flex-shrink-0">
              <Link 
                to="/login" 
                className="w-full sm:w-auto px-[28px] py-[12px] bg-white text-[#0B0F19] hover:bg-slate-200 text-lg rounded-full font-semibold shadow-[0_0_40px_rgba(255,255,255,0.15)] tracking-[0.18em] flex items-center justify-center gap-2 transition-all whitespace-nowrap"
              >
                Start free trial<ArrowRight size={20} aria-hidden="true" />
              </Link>
            </motion.div>
            
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="w-full sm:w-auto flex-shrink-0">
              <button 
                onClick={() => document.getElementById('features').scrollIntoView({behavior: 'smooth'})} 
                className="w-full sm:w-auto px-[28px] py-[12px] text-lg rounded-full font-medium border border-white/10 hover:border-white/20 bg-white/[0.03] hover:bg-white/[0.06] text-white transition-all shadow-lg backdrop-blur-md flex items-center justify-center cursor-pointer whitespace-nowrap tracking-[0.18em]"
              >
                Read the docs
              </button>
            </motion.div>
          </motion.div>

          {/* 3D Parallax Hero Image Mockup - FinTech Style */}
          <motion.div 
            initial={{ opacity: 0, y: 80 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 md:mt-10 w-full max-w-[1200px] perspective-[1500px]"
          >
            <motion.div 
              style={{ 
                rotateX, 
                rotateY,
                transformStyle: "preserve-3d"
              }}
              className="relative p-2 rounded-[2.5rem] border border-white/10 bg-white/[0.02] backdrop-blur-2xl shadow-[0_50px_100px_-20px_rgba(0,0,0,0.8),0_0_100px_0_rgba(99,102,241,0.2)]"
            >
              {/* Added explicit width/height for CLS prevention */}
              <img src="/analytics-marketing.png" alt="Thread Finance Dashboard" width="1084" height="610" className="w-full h-auto rounded-[2rem] block translate-z-[30px]" />
              
              {/* Floating decorative elements */}
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-purple-500/30 blur-[40px] rounded-full translate-z-[50px] pointer-events-none" />
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-emerald-500/20 blur-[50px] rounded-full translate-z-[50px] pointer-events-none" />
            </motion.div>
          </motion.div>
        </section>

        {/* Integrations Marquee */}
        <section className="w-full py-6 md:py-8 border-b border-white/5 overflow-hidden flex flex-col items-center bg-white/[0.01]">
          <p className="text-center text-slate-500 text-xs mb-4 uppercase tracking-[0.2em] font-semibold">Enterprise-Grade Infrastructure</p>
          <div className="marquee-container flex relative mask-image-fade w-full max-w-[1200px]">
            <div className="marquee-content flex gap-20 pr-20 animate-scroll-left w-max">
              {[1, 2].map((group) => (
                <React.Fragment key={group}>
                  <div className="flex items-center gap-3 text-xl font-bold text-slate-400/60 shrink-0 whitespace-nowrap"><ShieldCheck size={24} aria-hidden="true" /> Plaid</div>
                  <div className="flex items-center gap-3 text-xl font-bold text-slate-400/60 shrink-0 whitespace-nowrap"><CreditCard size={24} aria-hidden="true" /> Stripe</div>
                  <div className="flex items-center gap-3 text-xl font-bold text-slate-400/60 shrink-0 whitespace-nowrap">Chase</div>
                  <div className="flex items-center gap-3 text-xl font-bold text-slate-400/60 shrink-0 whitespace-nowrap">Bank of America</div>
                  <div className="flex items-center gap-3 text-xl font-bold text-slate-400/60 shrink-0 whitespace-nowrap">Wells Fargo</div>
                  <div className="flex items-center gap-3 text-xl font-bold text-slate-400/60 shrink-0 whitespace-nowrap">Capital One</div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        {/* Bento Box Features - Glassmorphic */}
        <section id="features" className="w-full py-10 md:py-12 px-6 relative flex flex-col items-center">
          <div className="w-full max-w-[1200px] mx-auto">
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="mb-10"
            >
              <h2 className="text-[clamp(2.5rem,5vw,4rem)] font-bold mb-6 tracking-tighter leading-tight max-w-[800px]">Programmatic financial execution.</h2>
              <p className="text-slate-400 text-xl md:text-2xl max-w-[700px] font-light">
                Leave the spreadsheets behind. Thread provides the primitives required to automate your entire financial stack.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 auto-rows-[minmax(220px,auto)]">
              
              {/* Large Card 1 */}
              <SpotlightCard className="md:col-span-2 lg:col-span-2 lg:row-span-2" spotColor="rgba(99,102,241,0.08)">
                <div className="flex-1">
                  <div className="w-14 h-14 bg-indigo-500/10 rounded-2xl flex items-center justify-center mb-8 border border-indigo-500/20 shadow-[0_0_30px_rgba(99,102,241,0.15)]">
                    <Activity size={26} className="text-indigo-400" aria-hidden="true" />
                  </div>
                  <h3 className="text-3xl font-bold mb-4 tracking-tight text-white">Visual Node Routing</h3>
                  <p className="text-slate-400 text-lg leading-relaxed font-light">
                    Construct complex financial logic using a visual node editor. Build bespoke waterfalls that distribute income across taxes, savings, and investments with exact precision.
                  </p>
                </div>
                <div className="mt-12 p-2 bg-white/[0.03] rounded-2xl border border-white/10 backdrop-blur-md">
                  <img src="/funds-marketing.png" alt="Routing Nodes" width="800" height="450" loading="lazy" className="w-full h-auto rounded-xl opacity-90" />
                </div>
              </SpotlightCard>

              {/* Medium Card 1 */}
              <SpotlightCard className="md:col-span-2 lg:col-span-2" spotColor="rgba(168,85,247,0.08)">
                <div className="w-14 h-14 bg-purple-500/10 rounded-2xl flex items-center justify-center mb-8 border border-purple-500/20 shadow-[0_0_30px_rgba(168,85,247,0.15)]">
                  <Zap size={26} className="text-purple-400" aria-hidden="true" />
                </div>
                <h3 className="text-2xl font-bold mb-3 tracking-tight text-white">Event-Driven Architecture</h3>
                <p className="text-slate-400 text-lg leading-relaxed font-light">
                  React instantly to webhooks. The microsecond your direct deposit is detected, our engine intercepts the payload and executes your ruleset autonomously.
                </p>
              </SpotlightCard>

              {/* Small Card 1 */}
              <SpotlightCard className="md:col-span-1 lg:col-span-1" spotColor="rgba(52,211,153,0.08)">
                <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-6 border border-emerald-500/20 shadow-[0_0_20px_rgba(52,211,153,0.1)]">
                  <LinkIcon size={22} className="text-emerald-400" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold mb-2 tracking-tight text-white">Universal Plaid API</h3>
                <p className="text-slate-400 text-base leading-relaxed font-light">Instant connectivity to 11,000+ global financial institutions.</p>
              </SpotlightCard>

              {/* Small Card 2 */}
              <SpotlightCard className="md:col-span-1 lg:col-span-1" spotColor="rgba(244,63,94,0.08)">
                <div className="w-12 h-12 bg-rose-500/10 rounded-xl flex items-center justify-center mb-6 border border-rose-500/20 shadow-[0_0_20px_rgba(244,63,94,0.1)]">
                  <ShieldCheck size={22} className="text-rose-400" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold mb-2 tracking-tight text-white">Zero Trust Security</h3>
                <p className="text-slate-400 text-base leading-relaxed font-light">AES-256 encryption. Non-custodial routing. We never hold funds.</p>
              </SpotlightCard>

              {/* Wide Bottom Card */}
              <SpotlightCard className="md:col-span-2 lg:col-span-4 flex flex-col md:flex-row items-center gap-10 md:gap-16 p-10 md:p-16" spotColor="rgba(255,255,255,0.05)">
                <div className="flex-1">
                  <h3 className="text-3xl font-bold mb-4 tracking-tight text-white">Predictive Historical Sync</h3>
                  <p className="text-slate-400 text-lg leading-relaxed font-light">
                    Our machine learning models ingest your last 12 months of transactional data to automatically map recurring liabilities and establish your baseline expense threshold.
                  </p>
                </div>
                <div className="hidden md:flex flex-1 justify-center items-center">
                  <div className="relative flex items-center justify-center w-20 h-20 bg-white/[0.05] rounded-2xl border border-white/10 backdrop-blur-xl shadow-[0_0_30px_rgba(99,102,241,0.15)] overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 to-transparent"></div>
                    <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 12, ease: "linear" }} className="relative z-10">
                      <RefreshCw size={32} className="text-indigo-300" aria-hidden="true" />
                    </motion.div>
                  </div>
                </div>
              </SpotlightCard>

            </div>
          </div>
        </section>

        {/* Pricing Section - Minimalist Premium */}
        <section id="pricing" className="w-full py-10 md:py-12 px-6 relative flex flex-col items-center">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-[1000px] mx-auto text-center"
          >
            
            <h2 className="text-[clamp(2.5rem,5vw,4rem)] font-bold mb-6 tracking-tighter">One platform. One plan.</h2>
            <p className="text-slate-400 text-xl mb-8 font-light">Everything you need to automate your wealth, transparently priced.</p>

            <div className="flex justify-center w-full relative">
              {/* Premium Glow Behind Card */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] h-[90%] bg-indigo-600/30 blur-[120px] pointer-events-none z-0" />
              
              <SpotlightCard className="w-full max-w-[480px] !p-0 overflow-hidden shadow-[0_30px_60px_-15px_rgba(79,70,229,0.3)] relative z-10 bg-gradient-to-b from-white/[0.08] to-transparent border border-white/20" spotColor="rgba(255,255,255,0.15)">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-400 via-purple-400 to-emerald-400 z-20" />
                
                <div className="flex flex-col relative z-20 p-12">
                  <h3 className="text-4xl font-extrabold mb-6 tracking-tight text-white drop-shadow-md">Thread Pro</h3>
                  <div className="flex items-baseline justify-center gap-2 mb-10">
                    <span className="text-7xl font-extrabold tracking-tighter text-white drop-shadow-lg">$8</span>
                    <span className="text-slate-300 text-xl font-medium">/ month</span>
                  </div>
                  
                  <ul className="text-left flex flex-col gap-6 text-lg mb-14">
                    <li className="flex gap-4 items-center">
                      <div className="bg-emerald-500/20 p-1.5 rounded-full border border-emerald-500/30 shadow-[0_0_10px_rgba(52,211,153,0.3)]"><Check size={16} className="text-emerald-400" aria-hidden="true" /></div> 
                      <span className="text-white font-medium tracking-wide">Unlimited Institution Connections</span>
                    </li>
                    <li className="flex gap-4 items-center">
                      <div className="bg-emerald-500/20 p-1.5 rounded-full border border-emerald-500/30 shadow-[0_0_10px_rgba(52,211,153,0.3)]"><Check size={16} className="text-emerald-400" aria-hidden="true" /></div> 
                      <span className="text-white font-medium tracking-wide">Advanced Node Waterfalls</span>
                    </li>
                    <li className="flex gap-4 items-center">
                      <div className="bg-emerald-500/20 p-1.5 rounded-full border border-emerald-500/30 shadow-[0_0_10px_rgba(52,211,153,0.3)]"><Check size={16} className="text-emerald-400" aria-hidden="true" /></div> 
                      <span className="text-white font-medium tracking-wide">Millisecond Webhook Routing</span>
                    </li>
                    <li className="flex gap-4 items-center">
                      <div className="bg-emerald-500/20 p-1.5 rounded-full border border-emerald-500/30 shadow-[0_0_10px_rgba(52,211,153,0.3)]"><Check size={16} className="text-emerald-400" aria-hidden="true" /></div> 
                      <span className="text-white font-medium tracking-wide">Predictive ML Forecasting</span>
                    </li>
                  </ul>

                  <Link to="/login" className="block w-full py-5 text-xl font-bold rounded-2xl text-center bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white shadow-[0_0_30px_rgba(79,70,229,0.4)] transition-all">
                    Start your 30-day trial
                  </Link>
                  <p className="text-center text-sm text-slate-400 mt-6 font-medium">Cancel anytime. No credit card required.</p>
                </div>
              </SpotlightCard>
            </div>
          </motion.div>
        </section>
      </main>

      {/* Footer */}
      <footer className="w-full py-6 md:py-8 px-6 border-t border-white/5 text-center text-slate-500 bg-[#0B0F19]">
        <div className="flex items-center justify-center gap-3 mb-4 opacity-60">
          <Logo size={28} /> <span className="font-bold text-xl text-slate-300 tracking-tight">Thread</span>
        </div>
        <p className="text-sm">&copy; {new Date().getFullYear()} Thread Finance. All rights reserved.</p>
        <div className="flex justify-center gap-8 mt-4 text-sm font-medium">
          <Link to="/privacy" className="hover:text-slate-300 transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-slate-300 transition-colors">Terms of Service</Link>
          <Link to="/security" className="hover:text-slate-300 transition-colors">Security</Link>
        </div>
      </footer>

      <style>{`
        .mask-image-fade {
          mask-image: linear-gradient(to right, transparent, black 15%, black 85%, transparent);
          -webkit-mask-image: linear-gradient(to right, transparent, black 15%, black 85%, transparent);
        }
        @keyframes scroll-left {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-scroll-left {
          animation: scroll-left 40s linear infinite;
        }
        @keyframes glisten {
          0% { background-position: 0% center; }
          10% { background-position: 100% center; }
          20% { background-position: 0% center; }
          100% { background-position: 0% center; }
        }
        .animate-glisten {
          background-image: linear-gradient(
            110deg,
            #818cf8 0%,
            #c084fc 16%,
            #34d399 33%,
            #c084fc 45%,
            #ffffff 50%,
            #c084fc 55%,
            #34d399 66%,
            #c084fc 83%,
            #818cf8 100%
          );
          background-size: 300% auto;
          animation: glisten 8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
